import React, { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { cn } from "~/lib/cn";
import { updateUser } from "~/lib/functions/user";
import { isClerkAPIResponseError } from "~/lib/clerk";
import { authMiddleware } from "~/lib/auth-middleware";
import ImageLogoHorizontal from "~/img/logo/dcm-horizontal-1.png";
import { createNoIndexMeta } from "~/lib/shared/meta";

import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import {
  MultiStepForm,
  useMultiStepForm,
} from "~/components/ui/multi-step-form";
import { OtpInput } from "~/components/ui/otp-input";
import { FormField } from "~/components/ui/form-field";

import SocialMedia from "~/img/social-media.svg";
import InterfaceSuccess from "~/img/interface-success.svg";

import { UserSignupSchema } from "~/lib/schemas/user-auth";

import { useClerk } from "@clerk/tanstack-react-start";
import type z from "zod";

type UserSignupForm = z.infer<typeof UserSignupSchema>;

const saveUserSignup = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: unknown) => UserSignupSchema.parse(data))
  .handler(async ({ data, context }) => {
    try {
      const userId = context.user?.id;
      if (!userId) {
        throw new Error("User ID not found");
      }

      await updateUser({ data: { userId, user: data } });
      return {
        status: "success",
        message: "Preferences updated",
      };
    } catch (error: unknown) {
      throw new Error(`Failed to save update user: ${error}`, {
        cause: error,
      });
    }
  });

// --- Step Components ---

const CredentialsStep = ({
  onNext,
  isProcessing,
  error,
}: {
  onNext: () => Promise<void>;
  isProcessing: boolean;
  error?: string;
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<UserSignupForm>();
  const { next } = useMultiStepForm();

  const handleNext = async () => {
    try {
      await onNext();
      next();
    } catch {
      // Error is handled by the parent component, don't proceed to next step
    }
  };

  return (
    <div className="w-full flex-shrink-0 space-y-2 px-1">
      <div className="text-center">
        <img src={SocialMedia} alt="Social Media" className="mx-auto" />
        <h1 className="font-heading text-3xl">Unlock your dream coach</h1>
        <h3 className="text-text-secondary font-heading">
          Create a free account to see all coaches we found for you and connect
          with them
        </h3>
      </div>
      <div className="space-y-2">
        <FormField
          fullWidth
          label="Enter email or phone number"
          error={errors.email?.message || error}
        >
          <Input
            {...register("email")}
            type="email"
            placeholder="Enter email or phone number"
            error={errors.email?.message}
          />
        </FormField>
      </div>
      <div className="space-y-2">
        <FormField
          fullWidth
          label="Create Password"
          error={errors.password?.message}
        >
          <Input
            {...register("password")}
            type="password"
            placeholder="Create Password"
            error={errors.password?.message}
          />
        </FormField>
      </div>
      <div className="space-y-2">
        <FormField
          fullWidth
          label="Confirm Password"
          error={errors.password2?.message}
        >
          <Input
            {...register("password2")}
            type="password"
            placeholder="Confirm Password"
            error={errors.password2?.message}
          />
        </FormField>
      </div>
      <Button
        type="button"
        loading={isProcessing}
        disabled={isProcessing}
        className="mb-2 w-full"
        onClick={handleNext}
      >
        Create Account & Continue
      </Button>
      <div id="clerk-captcha" className="w-full" />
    </div>
  );
};

const OtpStep = ({
  onVerify,
  onResend,
  isProcessing,
  canResend,
  resendTimer,
  error,
  code,
  onOtpChange,
}: {
  onVerify: (code: string) => Promise<void>;
  onResend: () => void;
  isProcessing: boolean;
  canResend: boolean;
  resendTimer: number;
  error: string;
  code: string[];
  onOtpChange: (code: string[]) => void;
}) => {
  const { next } = useMultiStepForm();

  const handleVerify = async (code: string) => {
    try {
      await onVerify(code);
      next();
    } catch {
      // Error is handled by the parent component, don't proceed to next step
    }
  };

  return (
    <div className="w-full flex-shrink-0 px-1">
      <div className="space-y-2">
        <h1 className="font-heading text-3xl">Check your email</h1>
        <h3 className="text-text-secondary font-heading">
          We sent a code to your email. Please enter it below to confirm your
          account.
        </h3>
      </div>
      <div className="my-8 flex items-end gap-4 pl-1 transition-all duration-300 ease-in-out">
        <OtpInput
          value={code}
          onChange={onOtpChange}
          onComplete={handleVerify}
          error={error}
        />
      </div>
      <Button
        type="button"
        className="mb-2 w-full"
        onClick={onResend}
        disabled={!canResend}
        loading={isProcessing && !canResend}
      >
        {canResend ? "Resend code" : `Resend code (${resendTimer}s)`}
      </Button>
    </div>
  );
};

const SuccessStep = ({ onComplete }: { onComplete: () => void }) => (
  <div className="w-full flex-shrink-0 space-y-2 px-1">
    <div className="text-center">
      <img src={InterfaceSuccess} alt="Success" className="mx-auto" />
      <h1 className="font-heading text-3xl">Account created!</h1>
      <h3 className="text-text-secondary font-heading">
        You're all set! Click below to see all your matches.
      </h3>
    </div>
    <Button type="button" className="mb-2 w-full" onClick={onComplete}>
      See my coach matches
    </Button>
    <div id="clerk-captcha" className="w-full" />
  </div>
);

export const Route = createFileRoute("/_auth/signup/")({
  head: () => {
    const metaConfig = createNoIndexMeta(
      "Sign Up – Dream Coach Match",
      "Create your free account to find your dream coach and unlock access to our coaching marketplace.",
    );

    return {
      ...metaConfig,
      links: [{ rel: "canonical", href: "https://dreamcoachmatch.com/signup" }],
    };
  },
  component: SignUpPage,
});

interface SignUpProps {
  isModalView?: boolean;
  onSuccess?: () => void;
}

const SignUp = ({ onSuccess, isModalView = false }: SignUpProps) => {
  const navigate = useNavigate();
  const clerk = useClerk();

  const form = useForm<UserSignupForm>({
    resolver: zodResolver(UserSignupSchema),
    defaultValues: {
      email: "",
      phoneNumber: undefined,
      password: "",
      password2: "",
      clerkUserId: "",
    },
  });
  const { handleSubmit, setValue, getValues } = form;

  const [isProcessing, setIsProcessing] = useState(false);
  const [state, setState] = useState({
    code: ["", "", "", "", "", ""],
    error: "",
    resendTimer: 60,
    canResend: false,
  });

  const saveUserMutation = useMutation({
    mutationFn: (data: UserSignupForm) => saveUserSignup({ data }),
    onSuccess: () => {
      // This will be called by the MultiStepForm's context
    },
    onError: (error: unknown) => {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : String(error),
      }));
    },
  });

  // OTP handlers
  const startResendTimer = () => {
    setState((prev) => ({ ...prev, resendTimer: 60, canResend: false }));
    const timer = setInterval(() => {
      setState((prev) => {
        if (1 >= prev.resendTimer) {
          clearInterval(timer);
          return { ...prev, resendTimer: 0, canResend: true };
        }
        return { ...prev, resendTimer: prev.resendTimer - 1 };
      });
    }, 1000);
  };

  const handleSendOtp = async (): Promise<void> => {
    const isValid = await form.trigger(["email", "password", "password2"]);
    if (!isValid) {
      throw new Error("Form validation failed");
    }

    startResendTimer();
    setState((prev) => ({ ...prev, error: "" }));
    setIsProcessing(true);
    const emailAddress = getValues("email");
    const password = getValues("password");

    try {
      if (!clerk?.client) {
        throw new Error("Clerk client not available");
      }

      await clerk.client.signUp.create({ emailAddress, password });
      await clerk.client.signUp.prepareEmailAddressVerification();

      setIsProcessing(false);
    } catch (err) {
      setIsProcessing(false);
      if (isClerkAPIResponseError(err)) {
        setState((prev) => ({
          ...prev,
          error:
            err.errors[0]?.longMessage || "Failed to create sign up attempt",
        }));
      } else {
        setState((prev) => ({
          ...prev,
          error: "An unexpected error occurred.",
        }));
      }
      throw err; // Re-throw to prevent step progression
    }
  };

  const handleVerifyOtp = async (code: string): Promise<void> => {
    setIsProcessing(true);

    try {
      if (!clerk?.client) {
        throw new Error("Clerk client not available");
      }

      const signUpAttempt =
        await clerk.client.signUp.attemptEmailAddressVerification({
          code,
        });

      if ("complete" !== signUpAttempt.status) {
        setIsProcessing(false);
        throw new Error("OTP verification incomplete");
      }
      setValue("clerkUserId", signUpAttempt.createdUserId as string);

      const formData = {
        ...getValues(),
        clerkUserId: signUpAttempt.createdUserId as string,
      };
      await saveUserMutation.mutateAsync(formData);

      await clerk.setActive({
        session: signUpAttempt.createdSessionId,
      });
    } catch (err) {
      setIsProcessing(false);
      if (isClerkAPIResponseError(err)) {
        setState((prev) => ({
          ...prev,
          error: err.errors[0]?.longMessage || "Failed to verify OTP code",
        }));
      } else {
        setState((prev) => ({
          ...prev,
          error: "An unexpected error occurred.",
        }));
      }
      throw err; // Re-throw to prevent step progression
    }
  };

  const handleSuccess = () => {
    if (onSuccess) {
      onSuccess();
    } else {
      navigate({ to: "/discovery/steps/processing" });
    }
  };

  const handleOtpChange = (code: string[]) => {
    setState((prev) => ({ ...prev, code: [...code], error: "" }));
  };

  return (
    <div className="flex flex-col items-center justify-center gap-6">
      <img
        src={ImageLogoHorizontal}
        alt="Dream Coach Match"
        className="w-[203px]"
      />
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(() => {
            /* Handled by step buttons */
          })}
        >
          <input {...form.register("clerkUserId")} type="hidden" />
          <Card
            className={cn("sm:w-145 w-100 flex flex-col p-6 hover:shadow", {
              "border-none p-0 shadow-none hover:shadow-none": isModalView,
            })}
          >
            <MultiStepForm>
              <CredentialsStep
                onNext={handleSendOtp}
                isProcessing={isProcessing}
                error={state.error}
              />
              <OtpStep
                onVerify={handleVerifyOtp}
                onResend={handleSendOtp}
                isProcessing={isProcessing}
                canResend={state.canResend}
                resendTimer={state.resendTimer}
                error={state.error}
                code={state.code}
                onOtpChange={handleOtpChange}
              />
              <SuccessStep onComplete={handleSuccess} />
            </MultiStepForm>

            <div className="font-400 mt-4 text-center text-sm">
              Already have an account?{" "}
              <a
                href="/signin"
                className="text-text-primary/100 font-600 hover:underline"
              >
                Log in
              </a>
            </div>
          </Card>
        </form>
      </FormProvider>
    </div>
  );
};

function SignUpPage() {
  return <SignUp />;
}

export { SignUp };

export const useFormLoader = () => {
  return null;
};
